<template>
  <div>
    <BSectionLoader :loading="$loading.isLoading('getDiiaData')">
      <div>
        <div class="mb-3">
          Зчитайте QR-код сканером у застосунку Дія або перейдіть за
          <a :href="diiaData.link" class="link" target="_blank">посиланням</a>
          та дотримуйтесь інструкцій.
        </div>

        <div class="d-flex justify-center mb-3">
          <v-img
            :src="diiaData.qrCode"
            cover
            aspect-ratio="1"
            inline
            width="256px"
            height="256px"></v-img>
        </div>

        <div class="mb-5 text-center">
          <span v-if="getDiiaDataTimeout > 0">
            Час життя QR коду: {{ diiaDataTimer }}
          </span>
          <div v-else>
            <b class="d-block text-warning mb-3">Час дії коду закінчився</b>
            <v-btn variant="outlined" color="success" @click="getDiiaData">
              Оновити код
            </v-btn>
          </div>
        </div>

        <v-btn variant="outlined" @click="dialog.close()"> Скасувати </v-btn>
      </div>
    </BSectionLoader>
  </div>
</template>

<script lang="ts">
import { computed, onBeforeUnmount, PropType, reactive, ref } from 'vue'
import { Dialog } from '@/dialog'
import { handleAsync } from 'best-modules/plugins'
import axios from '@/plugins/axios/index.js'
import { BSectionLoader } from 'best-modules/components'
import { padStart } from 'lodash'
import { socket, documentSignatureSingleChannel } from '@/socket.js'
import { initialize, hashFile } from '@/utils/signature'
import { DiiaRequestPayload } from '../types'

export default {
  name: 'SignDiia',
  components: { BSectionLoader },
  props: {
    dialog: {
      type: Object as PropType<Dialog>,
      required: true,
    },
  },
  setup(props) {
    const getDefaultSendCodeTimeout = (): number => {
      const t = localStorage.getItem('getDiiaDataTimeout')
      if (t) {
        return Number(t)
      } else {
        return 0
      }
    }

    let interval: ReturnType<typeof setInterval>
    const getDiiaDataTimeout = ref<number>(getDefaultSendCodeTimeout())
    const diiaDataTimer = computed<string>(() => {
      const minutes = Math.floor(getDiiaDataTimeout.value / 60)
      const seconds = getDiiaDataTimeout.value - minutes * 60

      return `${padStart(String(minutes), 2, '0')}:${padStart(
        String(seconds),
        2,
        '0'
      )}`
    })

    const setGetDiiaDataInterval = () => {
      clearInterval(interval)
      interval = setInterval(() => {
        if (getDiiaDataTimeout.value === 0) {
          clearInterval(interval)
          return
        }
        getDiiaDataTimeout.value -= 1
        localStorage.setItem(
          'shareDocumentCodeTimeout',
          String(getDiiaDataTimeout.value)
        )
      }, 1000)
    }

    const diiaData = reactive({
      qrCode: null as any,
      link: null as any,
    })

    const executeDiiaRequest = (payload: DiiaRequestPayload) => {
      const request = props.dialog.dialogData.diiaRequest

      if (typeof request === 'function') {
        return request(payload)
      }

      return axios({
        ...request,
        data: {
          ...request?.data,
          ...payload,
        },
      })
    }

    const getDiiaData = () => {
      return handleAsync('getDiiaData', async () => {
        await initialize('cloud')

        const hash = await hashFile(props.dialog.dialogData.fileToSign)
        const payload: DiiaRequestPayload = {
          algo: 'DSTU',
          hash,
          document: props.dialog.dialogData.document,
        }

        return executeDiiaRequest(payload).then((res: any) => {
          getDiiaDataTimeout.value = 180
          setGetDiiaDataInterval()
          Object.assign(diiaData, res?.data || res)
        })
      })
    }

    const handleDiiaSigned = (res: any) => {
      const signedDocument = res?.document || []
      const documentPath = props.dialog.dialogData.document?.path

      if (
        signedDocument.some((document: any) => document.path === documentPath)
      ) {
        props.dialog.submit(res)
      }
    }

    getDiiaData().then(() => {
      socket.on(documentSignatureSingleChannel, handleDiiaSigned)
    })

    onBeforeUnmount(() => {
      clearInterval(interval)
      socket.off(documentSignatureSingleChannel, handleDiiaSigned)
    })

    return { diiaData, getDiiaData, getDiiaDataTimeout, diiaDataTimer }
  },
}
</script>

<style scoped lang="scss"></style>
